import React, { useState, useEffect, useRef } from 'react';
import { contactsAPI } from 'services/apisService';

const CompanyInformation = ({ contact, onLeadUpdated, isLead = false, leadContactId, onContactUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValues, setTempValues] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const wrapperRef = useRef(null); 

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isEditing &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        const hasChanges = ['companyName', 'title', 'source', 'location'].some(
          field => (contact[field] || '') !== (tempValues[field] || '')
        );
        if (!hasChanges) {
          setIsEditing(false);
          setTempValues({});
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, tempValues, contact]);

  if (!contact) return null;

  const handleEdit = () => {
    setIsEditing(true);
    setTempValues({
      companyName: contact.companyName || '',
      title: contact.title || '',
      source: contact.source || '',
      location: contact.location || ''
    });
  };

   const handleSave = async () => {
    setIsUpdating(true);
    setUpdateError('');
    try {
      const updateData = {};
      const fields = ['companyName', 'title', 'source', 'location'];

      fields.forEach(field => {
        const originalValue = contact[field] || '';
        const newValue = tempValues[field] || '';
        if (originalValue !== newValue) {
          updateData[field] = newValue;
        }
      });

      if (Object.keys(updateData).length === 0) {
        setIsEditing(false);
        setTempValues({});
        return;
      }

      const updatedContact = await contactsAPI.updateContact(contact.id, updateData);

      Object.assign(contact, updatedContact);

      if (onContactUpdate) {
      onContactUpdate(updatedContact);
    }

      if (onLeadUpdated && isLead) {
        onLeadUpdated(leadContactId, updatedContact);
      }

      setIsEditing(false);
      setTempValues({});
    } catch (error) {
      console.error('Error updating company information:', error);
      setUpdateError('Failed to update company information. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };


  const handleCancel = () => {
    setIsEditing(false);
    setTempValues({});
  };

  const handleInputChange = (field, value) => {
    setTempValues(prev => ({ ...prev, [field]: value }));
  };
  

  const renderField = (field, label) => {
    const currentValue = contact[field] || '--';

    return (
      <div>
        <p className="text-sm font-bold text-[#3985b6] uppercase tracking-wider mb-1">{label}</p>

        {isEditing ? (
          <input
            type="text"
            value={tempValues[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className="w-full text-gray-800 p-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#3985b6] focus:border-transparent focus:bg-white"
            disabled={isUpdating}
            style={{ minHeight: '2.5rem', lineHeight: '1.5' }}
          />
        ) : (
          <p className="text-gray-800 note-wrap textarea-wrap bg-gray-50 p-2 rounded" style={{ minHeight: '2.5rem', lineHeight: '1.5' }}>{currentValue}</p>
        )}
      </div>
    );
  };

  return (
    <div
      ref={wrapperRef}
      className="bg-white rounded-lg shadow-md border border-[#e5f0f9] p-6 hover:shadow-lg transition-shadow duration-300 h-full"
    >
      {updateError && (
        <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {updateError}
        </div>
      )}

      <h3 className="text-lg font-semibold mb-4 text-[#3985b6] border-b border-[#e5f0f9] pb-2 flex items-center justify-between">
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Company Information
        </div>
        
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                className="bg-green-600 hover:bg-green-700 p-2 rounded text-white transition-colors"
                title="Save"
                disabled={isUpdating}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              <button
                onClick={handleCancel}
                className="bg-red-600 hover:bg-red-700 p-2 rounded text-white transition-colors"
                title="Cancel"
                disabled={isUpdating}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </>
          ) : (
            <button
              onClick={handleEdit}
              className="text-[#3985b6] hover:text-[#2d6f9a] p-1 rounded transition-colors"
              title="Edit company information"
              disabled={isUpdating}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
        </div>
      </h3>

      <div className="space-y-7 max-h-[52vh] px-1 pb-1 overflow-hidden overflow-y-auto">
        {renderField('companyName', 'Company')}
        {renderField('title', 'Title')}
        {renderField('source', 'Source')}
        {renderField('location', 'Location')}
      </div>
    </div>
  );
};

export default CompanyInformation;